import React, { useEffect, useState } from "react";
import { api, fmtDate } from "../lib/api";
import CardReadiness from "./CardReadiness";
import StatusPill from "./StatusPill";
import { Button } from "@/components/ui/button";
import { Zap, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export default function DispatchPanel({ projectId }) {
  const [mobs, setMobs] = useState([]);
  const [installers, setInstallers] = useState([]);
  const [crews, setCrews] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [running, setRunning] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const load = async () => {
    const [m, i, c, a] = await Promise.all([
      api.get("/mobilizations", { params: projectId ? { project_id: projectId } : {} }),
      api.get("/installers"),
      api.get("/crews"),
      api.get("/assignments"),
    ]);
    setMobs(m.data || []);
    setInstallers(i.data || []);
    setCrews(c.data || []);
    setAssignments(a.data || []);
  };
  useEffect(() => { load(); }, [projectId]);

  const runAuto = async () => {
    setRunning(true);
    try {
      const r = await api.post("/dispatch/auto", projectId ? { project_id: projectId } : {});
      setLastResult(r.data);
      const d = r.data?.dispatched ?? 0;
      const nr = r.data?.needs_review ?? 0;
      toast.success(`Dispatched ${d}, Needs Review ${nr}`);
      load();
    } catch {
      toast.error("Auto-dispatch failed");
    } finally { setRunning(false); }
  };

  const scheduled = mobs.filter((m) => m.status === "scheduled");
  const dispatched = mobs.filter((m) => m.status === "dispatched");
  const installerById = Object.fromEntries(installers.map((i) => [i.id, i]));

  return (
    <div className="space-y-3">
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm px-4 py-3 flex items-center gap-3 flex-wrap">
        <h3 className="text-sm font-semibold text-gray-900">Dispatch</h3>
        <span className="text-xs text-gray-500">Match scheduled mobs to installers by skills, leads & crews</span>
        <div className="ml-auto flex items-center gap-2">
          <Button onClick={runAuto} disabled={running || scheduled.length === 0} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="auto-dispatch-btn">
            <Zap className="w-3.5 h-3.5 mr-1" /> {running ? "Dispatching…" : `Auto Dispatch (${scheduled.length})`}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <section className="bg-white border border-gray-200 rounded-lg shadow-sm">
          <header className="px-3 py-2 border-b border-gray-200 bg-gray-50/50">
            <h3 className="text-sm font-semibold text-gray-900">Scheduled Mobilizations</h3>
            <p className="text-xs text-gray-500">{scheduled.length} ready for dispatch</p>
          </header>
          <div className="divide-y divide-gray-100 max-h-[460px] overflow-y-auto">
            {scheduled.length === 0 && <div className="text-xs text-gray-500 text-center py-6">Nothing scheduled.</div>}
            {scheduled.map((m) => (
              <div key={m.id} className="px-3 py-2 text-sm">
                <div className="font-medium text-gray-900 truncate">{m.title}</div>
                <div className="text-xs text-gray-500">{m.building} · F{m.floor} · {fmtDate(m.scheduled_start_date)}</div>
                <div className="mt-1 flex items-center gap-2"><StatusPill value={m.status} /><CardReadiness mobId={m.id} projectId={m.project_id} compact /></div>
              </div>
            ))}
          </div>
        </section>

        <section className="bg-white border border-gray-200 rounded-lg shadow-sm">
          <header className="px-3 py-2 border-b border-gray-200 bg-gray-50/50">
            <h3 className="text-sm font-semibold text-gray-900">Installers</h3>
            <p className="text-xs text-gray-500">{installers.filter((i) => i.active).length} active</p>
          </header>
          <div className="divide-y divide-gray-100 max-h-[460px] overflow-y-auto">
            {installers.map((i) => {
              const myCrew = crews.find((c) => c.id === i.crew_id);
              return (
                <div key={i.id} className="px-3 py-2 text-sm">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900">{i.name}</span>
                    {i.is_lead && <span className="text-[10px] bg-amber-50 text-amber-700 border border-amber-200 rounded-full px-1.5">Lead</span>}
                    {myCrew && <span className="text-[10px] bg-blue-50 text-blue-700 border border-blue-200 rounded-full px-1.5">{myCrew.name}</span>}
                  </div>
                  <div className="text-xs text-gray-500 truncate">{(i.skills || []).join(", ") || "no skills"}</div>
                </div>
              );
            })}
          </div>
        </section>

        <section className="bg-white border border-gray-200 rounded-lg shadow-sm">
          <header className="px-3 py-2 border-b border-gray-200 bg-gray-50/50">
            <h3 className="text-sm font-semibold text-gray-900">Dispatched</h3>
            <p className="text-xs text-gray-500">{dispatched.length} active</p>
          </header>
          <div className="divide-y divide-gray-100 max-h-[460px] overflow-y-auto">
            {dispatched.length === 0 && <div className="text-xs text-gray-500 text-center py-6">No dispatched mobilizations.</div>}
            {dispatched.map((m) => {
              const a = assignments.filter((x) => x.mobilization_id === m.id);
              return (
                <div key={m.id} className="px-3 py-2 text-sm">
                  <div className="font-medium text-gray-900 truncate">{m.title}</div>
                  <div className="text-xs text-gray-500">{fmtDate(m.scheduled_start_date)}</div>
                  <div className="text-xs text-gray-700 mt-0.5">
                    {a.map((x) => installerById[x.installer_id]?.name).filter(Boolean).join(", ") || "—"}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      </div>

      {lastResult && lastResult.details && lastResult.needs_review > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
          <div className="flex items-center gap-2 text-amber-800 text-sm font-medium mb-1">
            <AlertTriangle className="w-4 h-4" /> Needs Review ({lastResult.needs_review})
          </div>
          <ul className="text-xs text-amber-900 space-y-0.5 list-disc ml-5">
            {lastResult.details.filter((d) => d.reason).map((d, idx) => (
              <li key={idx}><span className="font-medium">{d.title}</span>: {d.reason}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
